import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Tabs, Table, Typography, Spin, Empty, Button, Space, message, Tooltip } from 'antd';
import { 
  EyeOutlined, 
  SwapOutlined, 
  DatabaseOutlined, 
  PlayCircleOutlined,
  ExpandOutlined,
  CompressOutlined 
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const DataPreview = ({ 
  selectedNode, 
  nodes, 
  edges, 
  isNodeSidebarOpen,
  onNodeUpdate 
}) => {
  // Component state
  const [isExpanded, setIsExpanded] = useState(false);
  const [height, setHeight] = useState(300);
  const [activeTab, setActiveTab] = useState('preview');
  const [previewData, setPreviewData] = useState(null);
  const [previewDataBySource, setPreviewDataBySource] = useState({});
  const [activePreviewTab, setActivePreviewTab] = useState('0');
  const [transformedData, setTransformedData] = useState(null);
  const [transformedDataBySource, setTransformedDataBySource] = useState({});
  const [activeTransformedTab, setActiveTransformedTab] = useState('0');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingTransformed, setLoadingTransformed] = useState(false);
  const [connectedDataSources, setConnectedDataSources] = useState([]);

  // Refs for resizing
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  // Authentication error handler
  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 204 || 
        (error.response?.status === 400)) {
      localStorage.removeItem('auth-token');
      message.error('Session expired. Please log in again.');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  // Get connected data sources for the selected node
  useEffect(() => {
    if (!selectedNode) {
      setConnectedDataSources([]);
      return;
    }

    const inputEdges = edges.filter(edge => edge.target === selectedNode.id);
    const connectedNodes = inputEdges.map(edge => {
      const sourceNode = nodes.find(node => node.id === edge.source);
      return sourceNode;
    }).filter(Boolean);

    setConnectedDataSources(connectedNodes);
  }, [selectedNode?.id, edges]);

  // Fetch preview data when node or connections change (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (selectedNode) {
        // Ensure we default to preview tab when node is selected
        if (activeTab !== 'preview') {
          setActiveTab('preview');
        }
        
        if (selectedNode.type === 'dataSource') {
          // For data sources, use the node's own config
          const config = selectedNode.data?.config;
          if (config && config.connectionId) {
            fetchPreviewData(config);
          }
        } else if (connectedDataSources.length > 0) {
          // For transformers/joins, use connected data sources
          const sourceToFetch = connectedDataSources[0];
          const config = sourceToFetch.data?.config;
          if (config) {
            fetchPreviewData(config);
          }
        }
      } else {
        setPreviewData(null);
        setPreviewDataBySource({});
        setTransformedData(null);
        setTransformedDataBySource({});
      }
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [selectedNode?.id, connectedDataSources]);

  // Invalidate cache when selectedNode config changes (when node is saved/updated)
  useEffect(() => {
    console.log('selectedNode_values: ', selectedNode)
    if (selectedNode) {
      // Clear cached data for this node so it refetches fresh data
      const nodeConfig = selectedNode?.data?.config;

      // Clear preview cache for connected sources
      if (connectedDataSources.length > 0) {
        setPreviewDataBySource(prev => {
          const newCache = { ...prev };
          connectedDataSources.forEach(source => {
            const config = source.data?.config;
            const connectionSourceIdentifier = config?.dataset || config?.table;
            const cacheKey = config?.connectionId + connectionSourceIdentifier;
            delete newCache[cacheKey];
          });
          return newCache;
        });

        // Refetch current preview data
        if (activeTab === 'preview') {
          const sourceToFetch = connectedDataSources[0];
          const config = sourceToFetch.data?.config;
          fetchPreviewData(config);
        }
      }

      // Clear transformed cache for this node
      const transformedCacheKey = selectedNode.id + (nodeConfig?.connectionName || '');
      setPreviewDataBySource(prev => {
        const newCache = { ...prev };
        delete newCache[transformedCacheKey];
        return newCache;
      });

      // Clear transformed data state
      setTransformedData(null);
      setTransformedDataBySource({});
    }
  }, [
    selectedNode?.data?.config?.connectionId,
    selectedNode?.data?.config?.connectionName,
    selectedNode?.data?.config?.transformationPlan,
    selectedNode?.data?.config?.joinAnalysis
  ]); // Reset when connection changes or when transformation/join is processed


  const fetchPreviewData = async (config) => {
    if (!selectedNode || !config) return;
    
    // For data source nodes, we don't need connected sources
    if (selectedNode.type === 'dataSource') {
      // Continue with fetch
    } else if (connectedDataSources.length === 0) {
      // For non-data source nodes, we need connected sources
      return;
    }
    
    setLoadingPreview(true);
    try {
      console.log("selectedNode: ", config)
      
      if (!config || !config.connectionId) {
        // message.warning('Data source not configured');
        return;
      }
      const connectionSourceIdentifier = config.dataset || config.table;
      const response = await axios.post('/workflow/sample-data', {
        connectionId: config.connectionId,
        sourceIdentifier: connectionSourceIdentifier,
        limit: 1000 // Limit for preview
      }, {
        headers: {
          'auth-token': localStorage.getItem('auth-token')
        }
      });

      if (response.data.success) {
        // Convert sample data format to preview data format
        const sampleData = response.data.data;
        const headers = sampleData.columnMappings?.map(col => col.name) || [];
        const rows = sampleData.sampleRows?.map(row => 
          headers.map(header => {
            const value = row.data[header];
            // Handle null, undefined, and string 'null' values
            if (value === null || value === undefined || value === 'null' || value === 'true') {
              return '';
            }
            return String(value);
          })
        ) || [];
        
        const formattedData = {
          headers: headers,
          data: rows,
          metadata: sampleData.metadata
        };
        setPreviewDataBySource(prev => ({
          ...prev,
          [config.connectionId + connectionSourceIdentifier]: formattedData
        }));
        setPreviewData(formattedData);

      } else {
        message.error('Failed to load preview data');
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error fetching preview data:', error);
      message.error('Failed to load preview data');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle tab change in preview section
  const handlePreviewTabChange = (tabKey) => {
    console.log('handlePreviewTabChange: ', tabKey)
    setActivePreviewTab(tabKey);
    const sourceIndex = parseInt(tabKey);
    
    // Get the source config and create cache key
    const sourceToFetch = connectedDataSources[sourceIndex];
    const config = sourceToFetch.data?.config;
    const connectionSourceIdentifier = config?.dataset || config?.table;
    const cacheKey = config?.connectionId + connectionSourceIdentifier;
    
    // Check if data for this source is already loaded
    if (!previewDataBySource[cacheKey]) {
      fetchPreviewData(config);
    }
  };




  // Resize functionality
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const newHeight = window.innerHeight - e.clientY;
    
    // Constrain height between 200px and 80% of window height
    const minHeight = 200;
    const maxHeight = window.innerHeight * 0.8;
    const constrainedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
    
    setHeight(constrainedHeight);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const toggleExpanded = () => {
    if (isExpanded) {
      setHeight(300);
    } else {
      setHeight(window.innerHeight * 0.7);
    }
    setIsExpanded(!isExpanded);
  };

  // Render data table
  const renderDataTable = (data, loading) => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading data...</div>
        </div>
      );
    }

    if (!data || !data.headers || !data.data) {
      return (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No data available"
        />
      );
    }

    const columns = data.headers.map((header, index) => ({
      title: header,
      dataIndex: index.toString(),
      key: index.toString(),
      ellipsis: true,
      width: 150,
      align: 'left'
    }));

    const dataSource = data.data.map((row, index) => {
      const record = { key: index };
      row.forEach((cell, cellIndex) => {
        record[cellIndex.toString()] = cell;
      });
      return record;
    });

    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        size="small"
        scroll={{ x: true, y: height - 150 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`
        }}
      />
    );
  };

  // Render tabs for multiple data sources (joins) or single data table
  const renderDataSourceTabs = () => {
    // For data source nodes, just show the single data table
    if (selectedNode?.type === 'dataSource') {
      return renderDataTable(previewData, loadingPreview);
    }

    // For joins with multiple connected sources, show tabs
    if (selectedNode?.type === 'join' && connectedDataSources.length > 1) {
      const tabNames = connectedDataSources.map((e) => {
        const config = e.data.config
        let name = config.node_name || `${config.connectionName}`
        if(config.dataset){
          name +=  ` - ${config.dataset}`
        }
        return name
      })

      return (
        <Tabs 
          size="small" 
          activeKey={activePreviewTab}
          onChange={handlePreviewTabChange}
        >
          {tabNames.map((source, index) => {
            // Create cache key for this source
            const sourceConfig = connectedDataSources[index]?.data?.config;
            const connectionSourceIdentifier = sourceConfig?.dataset || sourceConfig?.table;
            const cacheKey = sourceConfig?.connectionId + connectionSourceIdentifier;
            const sourceData = previewDataBySource[cacheKey];
            const isLoadingThisSource = loadingPreview && activePreviewTab === index.toString();
            
            return (
              <TabPane 
                tab={source} 
                key={index.toString()}
              >
                {renderDataTable(sourceData, isLoadingThisSource)}
              </TabPane>
            );
          })}
        </Tabs>
      );
    }

    // For transformers or single-source scenarios
    return renderDataTable(previewData, loadingPreview);
  };


  // Check if transformed tab should be enabled
  const isTransformedTabEnabled = () => {
    const nodeConfig = selectedNode?.data?.config;
    // console.log('selectedNode: ', selectedNode, nodeConfig)

    return nodeConfig?.connectionName || nodeConfig?.isProcessed;
  };

  const changeTab = (tabName) => {
    console.log('tabName: ', tabName)
    setActiveTab(tabName)
    
    if(tabName === 'transformed' && isTransformedTabEnabled()){
      // Check if transformed data is already cached
      const nodeConfig = selectedNode?.data?.config;
      const cacheKey = selectedNode.id + (nodeConfig?.connectionName || '');
      
      if (!previewDataBySource[cacheKey]) {
        // Fetch transformed data using node's connectionName
        fetchTransformedData();
      } else {
        // Use cached transformed data
        setTransformedData(previewDataBySource[cacheKey]);
      }
    }
  }

  const fetchTransformedData = async () => {
    if (!selectedNode) return;

    // Check if node is saved/processed
    const nodeConfig = selectedNode?.data?.config;
    if (!nodeConfig?.connectionName && !nodeConfig?.isProcessed) {
      message.warning('Node must be saved/processed first');
      return;
    }
    console.log('nodeConfig: ', nodeConfig)
    setLoadingTransformed(true);
    try {
      // Find the transformed connection
      const transformedConnectionResponse = await axios.post('/workflow/connections', {transformer: true}, {
        headers: {
          'auth-token': localStorage.getItem('auth-token'),
          'Content-Type': 'application/json'
        }
      });

      if (transformedConnectionResponse.data.success) {
        const connections = transformedConnectionResponse.data.data;
        console.log('connections: ', connections, nodeConfig.connectionName)
        const transformedConnection = connections.find(conn => conn.name === nodeConfig.connectionName);
        
        if (transformedConnection) {
          const response = await axios.post('/workflow/sample-data', {
            connectionId: transformedConnection._id,
            sourceIdentifier: transformedConnection.availableSources?.[0]?.identifier,
            limit: 1000
          }, {
            headers: {
              'auth-token': localStorage.getItem('auth-token')
            }
          });

          if (response.data.success) {
            const sampleData = response.data.data;
            const headers = sampleData.columnMappings?.map(col => col.name) || [];
            const rows = sampleData.sampleRows?.map(row => 
              headers.map(header => {
                const value = row.data[header];
                // Handle null, undefined, and string 'null' values
                if (value === null || value === undefined || value === 'null' || value === 'true') {
                  return '';
                }
                return String(value);
              })
            ) || [];
            
            const formattedData = {
              headers: headers,
              data: rows,
              metadata: sampleData.metadata
            };

            // Cache the transformed data
            const cacheKey = selectedNode.id + (nodeConfig?.connectionName || '');
            setPreviewDataBySource(prev => ({
              ...prev,
              [cacheKey]: formattedData
            }));
            
            setTransformedData(formattedData);
          } else {
            message.error('Failed to load transformed data');
          }
        } else {
          message.error('Transformed connection not found');
        }
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error fetching transformed data:', error);
      message.error('Failed to load transformed data');
    } finally {
      setLoadingTransformed(false);
    }
  };

  // Don't render if no node is selected
  if (!selectedNode) {
    return null;
  }

  // Don't render for start and output nodes
  if (selectedNode.type === 'start' || selectedNode.type === 'output') {
    return null;
  }

  // For data sources, check if they have configuration
  if (selectedNode.type === 'dataSource' && !selectedNode.data?.config?.connectionId) {
    return null;
  }

  // For transformers and joins, check if they have connected sources
  if ((selectedNode.type === 'transformer' || selectedNode.type === 'join') && connectedDataSources.length === 0) {
    return null;
  }

  return (
    <div 
      ref={containerRef}
      style={{
        position: 'fixed',
        paddingLeft: 10,
        bottom: 0,
        left: 0,
        right: isNodeSidebarOpen ? '400px' : 0,
        height: `${height}px`,
        backgroundColor: '#fff',
        borderTop: '1px solid #e8e8e8',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          height: '6px',
          marginTop: 5,
          backgroundColor: '#f0f0f0',
          cursor: 'row-resize',
          borderTop: '1px solid #d9d9d9',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3px',
          borderRadius: '8px'
        }}
      >
        <div 
          style={{ 
            width: '40px', 
            height: '4px', 
            backgroundColor: '#bfbfbf', 
            borderRadius: '2px' ,
          }} 
        />
      </div>


      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Tabs 
          activeKey={activeTab} 
          onChange={changeTab}
          size="small"
          style={{ height: '100%', paddingLeft: 5 }}
        >
          <TabPane 
            tab={
              <span>
                <EyeOutlined />
                Preview
              </span>
            } 
            key="preview"
            style={{ height: '100%', padding: '8px 16px' }}
          >
            {renderDataSourceTabs()}
          </TabPane>
          
          {selectedNode.data.label !== 'Data Source' && <TabPane 
            tab={
              <span>
                <SwapOutlined />
                Transformed
                {transformedData && <span style={{ marginLeft: 4, color: '#52c41a' }}>✓</span>}
              </span>
            } 
            key="transformed"
            disabled={!isTransformedTabEnabled()}
            style={{ height: '100%', padding: '8px 16px' }}
          >
            {!isTransformedTabEnabled() ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Node must be saved/processed first"
                />
              </div>
            ) : (
              renderDataTable(transformedData, loadingTransformed)
            )}
          </TabPane>}
        </Tabs>
      </div>
    </div>
  );
};

export default DataPreview;